import { FirebaseError } from 'firebase/app'
import {
  GoogleAuthProvider,
  signInWithCredential,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  UserCredential
} from 'firebase/auth'
import { auth } from '../firebase'
import { safeSessionSetItem } from './localStorage'

// Clave de sessionStorage que persiste el origen exacto del flujo de Google
// (onetap vs botón de "Continuar con Google") para que la página de
// /onboarding pueda distinguir ambos al persistir `authProvider`.
const GOOGLE_AUTH_SOURCE_SESSION_KEY = 'barmanager.googleAuthSource'

// `additionalUserInfo` está en el runtime de UserCredential desde hace
// años, pero las definiciones públicas que `firebase/auth` re-exporta
// no lo exponen en este SDK. Definimos un tipo local para no perder
// `isNewUser`, que es el dato que nos dice si tenemos que ir a
// /onboarding o a /dashboard.
type UserCredentialWithInfo = UserCredential & {
  additionalUserInfo?: { isNewUser: boolean } | null
}

export type GoogleAuthSource =
  | 'onetap'
  | 'hero'
  | 'login'
  | 'signup'

export type GoogleAuthOutcome =
  | { status: 'success', isNewUser: boolean, source: GoogleAuthSource }
  | { status: 'cancelled', source: GoogleAuthSource }
  | { status: 'error', code: string, message: string, source: GoogleAuthSource }

let inFlight = false
const listeners = new Set<() => void>()

export const isGoogleAuthInFlight = (): boolean => inFlight

export const subscribeGoogleAuth = (cb: () => void): (() => void) => {
  listeners.add(cb)
  return () => { listeners.delete(cb) }
}

const setInFlight = (value: boolean): void => {
  inFlight = value
  listeners.forEach((fn) => fn())
}

const buildProvider = (): GoogleAuthProvider => {
  const provider = new GoogleAuthProvider()
  provider.setCustomParameters({ prompt: 'select_account' })
  return provider
}

const handleCredentialError = (error: unknown, source: GoogleAuthSource): GoogleAuthOutcome => {
  const code = (error as FirebaseError)?.code ?? 'unknown'
  const message = (error as FirebaseError)?.message ?? String(error)
  if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
    return { status: 'cancelled', source }
  }
  return { status: 'error', code, message, source }
}

const notifyOutcome = (outcome: GoogleAuthOutcome): void => {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent('google-auth-outcome', { detail: outcome }))
}

export const loginWithGooglePopup = async (source: GoogleAuthSource): Promise<GoogleAuthOutcome> => {
  if (inFlight) return { status: 'cancelled', source }
  safeSessionSetItem(GOOGLE_AUTH_SOURCE_SESSION_KEY, source)
  setInFlight(true)
  try {
    const provider = buildProvider()
    let credential: UserCredential
    try {
      credential = await signInWithPopup(auth, provider)
    } catch (error) {
      const code = (error as FirebaseError)?.code
      if (code === 'auth/popup-blocked' || code === 'auth/operation-not-supported-in-this-environment') {
        await signInWithRedirect(auth, buildProvider())
        return { status: 'cancelled', source }
      }
      return handleCredentialError(error, source)
    }
    const isNewUser = (credential as UserCredentialWithInfo).additionalUserInfo?.isNewUser ?? false
    const outcome: GoogleAuthOutcome = { status: 'success', isNewUser, source }
    notifyOutcome(outcome)
    return outcome
  } finally {
    setInFlight(false)
  }
}

export const loginWithGoogleOneTap = async (idToken: string, source: GoogleAuthSource = 'onetap'): Promise<GoogleAuthOutcome> => {
  if (inFlight) return { status: 'cancelled', source }
  safeSessionSetItem(GOOGLE_AUTH_SOURCE_SESSION_KEY, source)
  setInFlight(true)
  try {
    const credential = GoogleAuthProvider.credential(idToken)
    let result: UserCredential
    try {
      result = await signInWithCredential(auth, credential)
    } catch (error) {
      return handleCredentialError(error, source)
    }
    const isNewUser = (result as UserCredentialWithInfo).additionalUserInfo?.isNewUser ?? false
    const outcome: GoogleAuthOutcome = { status: 'success', isNewUser, source }
    notifyOutcome(outcome)
    return outcome
  } finally {
    setInFlight(false)
  }
}

export const consumeRedirectResult = async (): Promise<GoogleAuthOutcome | null> => {
  if (inFlight) return null
  // El redirect siempre se origina en el botón de "Continuar con Google"
  // (nunca en One Tap), así que etiquetamos la fuente como `hero`.
  safeSessionSetItem(GOOGLE_AUTH_SOURCE_SESSION_KEY, 'hero')
  setInFlight(true)
  try {
    const result = await getRedirectResult(auth)
    if (!result) return null
    const isNewUser = (result as UserCredentialWithInfo).additionalUserInfo?.isNewUser ?? false
    const outcome: GoogleAuthOutcome = { status: 'success', isNewUser, source: 'hero' }
    notifyOutcome(outcome)
    return outcome
  } catch (error) {
    return handleCredentialError(error, 'hero')
  } finally {
    setInFlight(false)
  }
}
